import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth.store.js';
import { useClients } from '../hooks/useClients.js';
import { useCaeRequestsCountToday } from '../hooks/useCaeRequests.js';
import PageHeader from '../components/PageHeader.jsx';

const SERVICES = [
  { name: 'arca-api (negocio)', url: import.meta.env.VITE_ARCA_API_URL || 'https://arca.mainnet.com.ar/api/health' },
  { name: 'arca-admin-api', url: `${(import.meta.env.VITE_API_URL || 'http://localhost:3202/admin').replace(/\/admin$/, '')}/health` },
];

function StatusDot({ ok, loading }) {
  if (loading) return <span className="w-2 h-2 rounded-full bg-slate-600 animate-pulse inline-block" />;
  return <span className={`w-2 h-2 rounded-full inline-block ${ok ? 'bg-emerald-400' : 'bg-red-400'}`} />;
}

function ServiceStatus({ name, url }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['health', name],
    queryFn: async () => (await fetch(url)).json(),
    refetchInterval: 30_000,
    retry: 1,
  });
  const ok = !isError && data?.status === 'ok';
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-3">
        <StatusDot ok={ok} loading={isLoading} />
        <span className="text-sm text-text-secondary">{name}</span>
      </div>
      <span className={`text-xs px-2 py-0.5 rounded font-medium ${ok ? 'text-emerald-400' : isLoading ? 'text-text-muted' : 'text-red-400'}`}>
        {isLoading ? 'verificando…' : ok ? 'online' : 'error'}
      </span>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="card">
      <p className="text-xs text-text-muted">{label}</p>
      <p className="text-2xl font-semibold text-text-primary mt-1">{value ?? '—'}</p>
    </div>
  );
}

export default function Dashboard() {
  const user = useAuthStore((s) => s.user);
  const clientsQuery = useClients();
  const caeTodayQuery = useCaeRequestsCountToday();

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader title={`Hola, ${user?.name || user?.email}`} subtitle="Estado general de arca-gateway" />

      {user?.role === 'superadmin' && (
        <div className="grid grid-cols-2 gap-4">
          <StatCard label="Clientes" value={clientsQuery.data?.length} />
          <StatCard label="Comprobantes hoy" value={caeTodayQuery.data?.count} />
        </div>
      )}

      <div className="card space-y-1">
        <h3 className="font-medium text-text-secondary mb-2">Estado de servicios</h3>
        <div className="divide-y divide-border">
          {SERVICES.map((s) => <ServiceStatus key={s.name} {...s} />)}
        </div>
      </div>
    </div>
  );
}
